import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  BookOpen,
  CalendarSync,
  Loader2,
  Plus,
  RefreshCw,
  Sparkles,
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/axiosConfig';
import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import BlockWeekSessionCard from './BlockWeekSessionCard';
import { loadLevelStyle, weekDayKeys } from './blockWeekSessionStyles';
import { acwrToHeatmapColor } from '../../utils/acwrHeatmapColor';
import UserRunningExerciseEditorModal from './UserRunningExerciseEditorModal';
import SessionPickerModal from './SessionPickerModal';

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

function sessionsNeedSpreadOptimization(sessions, weekStartYmd) {
  if (!sessions?.length || sessions.length < 2 || !weekStartYmd) return false;
  const sorted = [...sessions].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id
  );
  const weekDays = weekDayKeys(weekStartYmd);
  return sorted.every((s, i) => String(s.scheduled_date).slice(0, 10) === weekDays[i]);
}

function TimesheetDayColumn({ dayKey, label, sessions, busy, onEdit, onToggleSync, onReplace, onAddSession, acwr }) {
  const { t } = useLanguage();
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${dayKey}`,
    data: { type: 'day', dayKey },
  });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        'flex min-h-[10rem] min-w-[9.5rem] flex-1 flex-col rounded-lg border border-border/80 bg-muted/20',
        isOver && 'ring-2 ring-primary/40 bg-primary/5'
      )}
    >
      <div className='shrink-0 border-b border-border/60 px-2 py-1.5 text-center'>
        <p className='text-[10px] font-semibold uppercase tracking-wide text-muted-foreground'>{label}</p>
        <p className='text-[11px] font-medium tabular-nums'>{dayKey.slice(5)}</p>
        {acwr != null && Number.isFinite(Number(acwr)) && (
          <span
            className='mt-1 inline-block rounded px-1.5 py-0.5 text-[9px] font-semibold tabular-nums text-white'
            style={{ backgroundColor: acwrToHeatmapColor(Number(acwr)) }}
            title={`ACWR ${acwr}`}
          >
            {Number(acwr).toFixed(2)}
          </span>
        )}
      </div>
      <div className='flex flex-1 flex-col gap-1.5 p-1.5'>
        {sessions.map((s) => (
          <BlockWeekSessionCard
            key={s.id}
            session={s}
            busy={busy}
            onEdit={onEdit}
            onToggleSync={onToggleSync}
            onReplace={onReplace}
          />
        ))}
        {sessions.length === 0 && (
          <Button
            type='button'
            variant='outline'
            className='h-10 w-full shrink-0 border-dashed text-muted-foreground hover:text-foreground'
            disabled={busy}
            onClick={() => onAddSession?.(dayKey)}
            title={t('periodization.week.addSession')}
          >
            <Plus className='h-4 w-4' />
          </Button>
        )}
      </div>
    </div>
  );
}

function DragPreview({ session }) {
  if (!session) return null;
  const styles = loadLevelStyle(session.load_level);
  return (
    <div className={cn('w-44 rounded-lg border p-2 shadow-lg', styles.card)}>
      <p className='truncate text-xs font-medium'>{session.user_running_exercise?.title || session.title}</p>
    </div>
  );
}

export default function PeriodizationWeekSessionsModal({
  open,
  onOpenChange,
  micro,
  phase,
  blockId,
  onUpdated,
}) {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [acwrByDay, setAcwrByDay] = useState({});
  const [maxWeekAcwr, setMaxWeekAcwr] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [editUrexId, setEditUrexId] = useState(null);
  const [pickerTarget, setPickerTarget] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const dayKeys = useMemo(() => weekDayKeys(micro?.week_start), [micro?.week_start]);

  const sessionsByDay = useMemo(() => {
    const map = Object.fromEntries(dayKeys.map((d) => [d, []]));
    for (const s of sessions) {
      const key = String(s.scheduled_date).slice(0, 10);
      if (map[key]) map[key].push(s);
      else if (dayKeys.length) {
        const fallback = dayKeys[0];
        map[fallback] = map[fallback] || [];
        map[fallback].push(s);
      }
    }
    for (const d of dayKeys) {
      map[d].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);
    }
    return map;
  }, [sessions, dayKeys]);

  const unsyncedCount = sessions.filter((s) => !s.calendar_synced).length;

  const reloadBlock = useCallback(async () => {
    if (!blockId || !onUpdated) return;
    try {
      const res = await api.get(`/api/training/periodization/blocks/${blockId}`);
      onUpdated(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [blockId, onUpdated]);

  const load = useCallback(async () => {
    if (!micro?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/training/periodization/microcycles/${micro.id}/sessions`);
      let loaded = res.data.sessions || [];
      if (sessionsNeedSpreadOptimization(loaded, micro.week_start)) {
        try {
          const spreadRes = await api.post(
            `/api/training/periodization/microcycles/${micro.id}/week-sessions/optimize-spread`
          );
          loaded = spreadRes.data.sessions || loaded;
        } catch (spreadErr) {
          console.warn('week spread optimize:', spreadErr);
        }
      }
      setSessions(loaded);
      try {
        const acwrRes = await api.get(
          `/api/training/periodization/microcycles/${micro.id}/acwr-by-day`
        );
        const map = {};
        for (const row of acwrRes.data?.days || []) {
          if (row?.date) map[row.date] = row.acwr;
        }
        setAcwrByDay(map);
        setMaxWeekAcwr(acwrRes.data?.max_week_acwr ?? null);
      } catch (acwrErr) {
        console.warn('week acwr:', acwrErr);
        setAcwrByDay({});
        setMaxWeekAcwr(null);
      }
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.sessionsLoadError'));
      setSessions([]);
      setAcwrByDay({});
      setMaxWeekAcwr(null);
    } finally {
      setLoading(false);
    }
  }, [micro?.id, micro?.week_start, t]);

  useEffect(() => {
    if (open && micro?.id) {
      setMessage(null);
      load();
    }
  }, [open, micro?.id, load]);

  const handleToggleSync = async (session, shouldSync) => {
    setBusyAction(`sync-${session.id}`);
    setError(null);
    try {
      const path = shouldSync
        ? `/api/training/periodization/week-sessions/${session.id}/sync`
        : `/api/training/periodization/week-sessions/${session.id}/unsync`;
      await api.post(path);
      await load();
      await reloadBlock();
      window.dispatchEvent(new Event('trainingUpdated'));
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.syncError'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleSyncAll = async () => {
    if (!micro?.id) return;
    setBusyAction('sync-all');
    setError(null);
    setMessage(null);
    try {
      const res = await api.post(
        `/api/training/periodization/microcycles/${micro.id}/week-sessions/sync-all`
      );
      setSessions(res.data.sessions || []);
      setMessage(
        t('periodization.week.syncAllSuccess', {
          count: res.data.synced_count ?? 0,
        })
      );
      await load();
      await reloadBlock();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.syncError'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleRegenerate = async () => {
    if (!micro?.id) return;
    if (!window.confirm(t('periodization.week.regenerateConfirm'))) return;
    setBusyAction('regenerate');
    setError(null);
    setMessage(null);
    try {
      const res = await api.post(
        `/api/training/periodization/microcycles/${micro.id}/week-sessions/regenerate`
      );
      setSessions(res.data.sessions || []);
      setMessage(
        t('periodization.week.regenerateSuccess', {
          count: res.data.sessions_created ?? 0,
        })
      );
      await load();
      await reloadBlock();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.regenerateError'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleFillWeek = async () => {
    if (!blockId || !micro?.id) return;
    setBusyAction('fill');
    setError(null);
    setMessage(null);
    try {
      const res = await api.post(
        `/api/training/periodization/blocks/${blockId}/fill-week/${micro.id}`,
        { force: false }
      );
      const filled = res.data?.results?.filter((r) => r.filled)?.length ?? 0;
      setMessage(
        filled > 0
          ? t('periodization.week.fillSuccess', { count: filled })
          : t('periodization.week.fillNoChange')
      );
      await load();
      await reloadBlock();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.fillError'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleAssignLibrary = async () => {
    if (!blockId || !micro?.id) return;
    setBusyAction('assign');
    setError(null);
    setMessage(null);
    try {
      const res = await api.post(
        `/api/training/periodization/blocks/${blockId}/weeks/${micro.id}/assign-library`,
        { force: false }
      );
      const assigned = res.data?.assigned ?? res.data?.filled ?? 0;
      setMessage(
        assigned > 0
          ? t('periodization.week.assignSuccess', { count: assigned })
          : t('periodization.week.assignNoChange')
      );
      await load();
      await reloadBlock();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.assignError'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleDragEnd = async (event) => {
    setActiveDrag(null);
    const { active, over } = event;
    if (!over || !active?.data?.current) return;
    const overId = String(over.id);
    if (!overId.startsWith('day-')) return;
    const targetDate = overId.slice(4);
    const { sessionId, scheduledDate } = active.data.current;
    if (!sessionId || targetDate === scheduledDate) return;

    setBusyAction(`move-${sessionId}`);
    setError(null);
    try {
      const res = await api.put(`/api/training/periodization/week-sessions/${sessionId}/move`, {
        scheduled_date: targetDate,
      });
      setSessions(res.data.sessions || []);
      await load();
      await reloadBlock();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.moveError'));
    } finally {
      setBusyAction(null);
    }
  };

  const handleSessionPicked = async (sess) => {
    if (!sess?.id || !pickerTarget) return;
    setPickerTarget(null);
    setBusyAction(pickerTarget.type === 'add' ? `add-${pickerTarget.dayKey}` : `replace-${pickerTarget.sessionId}`);
    setError(null);
    try {
      let res;
      if (pickerTarget.type === 'add') {
        res = await api.post(`/api/training/periodization/microcycles/${micro.id}/week-sessions`, {
          scheduled_date: pickerTarget.dayKey,
          training_session_id: sess.id,
          name: sess.name,
        });
      } else {
        res = await api.post(
          `/api/training/periodization/week-sessions/${pickerTarget.sessionId}/assign-library`,
          { training_session_id: sess.id, name: sess.name }
        );
      }
      setSessions(res.data.sessions || []);
      await load();
      await reloadBlock();
      window.dispatchEvent(new Event('trainingUpdated'));
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.error || t('periodization.week.assignSessionError'));
    } finally {
      setBusyAction(null);
    }
  };

  const weekdayLabels = WEEKDAY_KEYS.map((key) => t(`periodization.week.days.${key}`));
  const isBusy = !!busyAction;

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className='max-w-[90vw] w-[90vw] gap-3' priority='nested'>
          <DialogHeader className='space-y-1 text-left'>
            <DialogTitle className='text-base'>
              {t('periodization.week.sessionsTitle', { week: micro?.week_number_in_block })}
            </DialogTitle>
            {micro && (
              <div className='flex flex-wrap items-center gap-2'>
                <p className='text-xs text-muted-foreground'>
                  {phase?.name && `${phase.name} · `}
                  {micro.week_start} → {micro.week_end}
                </p>
                {phase?.phase_kind && (
                  <Badge variant='secondary' className='text-[10px]'>
                    {phase.phase_kind}
                  </Badge>
                )}
                {micro.is_deload && (
                  <Badge variant='outline' className='text-[10px] ring-1 ring-amber-400'>
                    {t('periodization.deload')}
                  </Badge>
                )}
                {maxWeekAcwr != null && Number.isFinite(Number(maxWeekAcwr)) && (
                  <Badge
                    variant='outline'
                    className='text-[10px] tabular-nums'
                    style={{ borderColor: acwrToHeatmapColor(Number(maxWeekAcwr)) }}
                  >
                    {t('periodization.week.maxAcwr', { value: Number(maxWeekAcwr).toFixed(2) })}
                  </Badge>
                )}
              </div>
            )}
          </DialogHeader>

          <div className='flex flex-wrap items-center gap-2'>
            <Button
              type='button'
              size='sm'
              variant='secondary'
              className='gap-1.5'
              disabled={isBusy || sessions.length === 0}
              onClick={handleFillWeek}
            >
              {busyAction === 'fill' ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <Sparkles className='h-3.5 w-3.5' />
              )}
              {t('periodization.fillWeek', { week: micro?.week_number_in_block })}
            </Button>
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='gap-1.5'
              disabled={isBusy || sessions.length === 0}
              onClick={handleAssignLibrary}
            >
              {busyAction === 'assign' ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <BookOpen className='h-3.5 w-3.5' />
              )}
              {t('periodization.assignLibrary', { week: micro?.week_number_in_block })}
            </Button>
            <Button
              type='button'
              size='sm'
              variant='default'
              className='gap-1.5'
              disabled={isBusy || unsyncedCount === 0}
              onClick={handleSyncAll}
            >
              {busyAction === 'sync-all' ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <CalendarSync className='h-3.5 w-3.5' />
              )}
              {t('periodization.week.syncAll', { count: unsyncedCount })}
            </Button>
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='gap-1.5'
              disabled={isBusy}
              onClick={handleRegenerate}
            >
              {busyAction === 'regenerate' ? (
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
              ) : (
                <RefreshCw className='h-3.5 w-3.5' />
              )}
              {t('periodization.week.regenerate')}
            </Button>
          </div>

          {message && <p className='text-xs text-primary'>{message}</p>}
          {error && <p className='text-xs text-destructive'>{error}</p>}

          {loading ? (
            <div className='flex justify-center py-12'>
              <Loader2 className='h-7 w-7 animate-spin text-primary' />
            </div>
          ) : (
            <>
              {sessions.length === 0 && (
                <p className='rounded-lg border border-dashed border-border bg-muted/20 p-3 text-center text-xs text-muted-foreground'>
                  {t('periodization.week.noSessions')}
                </p>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={pointerWithin}
                onDragStart={(e) => setActiveDrag(e.active?.data?.current?.session || null)}
                onDragEnd={handleDragEnd}
              >
                <div className='flex gap-2 overflow-x-auto pb-1'>
                  {dayKeys.map((dayKey, idx) => (
                    <TimesheetDayColumn
                      key={dayKey}
                      dayKey={dayKey}
                      label={weekdayLabels[idx]}
                      sessions={sessionsByDay[dayKey] || []}
                      busy={isBusy}
                      acwr={acwrByDay[dayKey]}
                      onEdit={(s) => setEditUrexId(s.user_running_exercise_id)}
                      onToggleSync={handleToggleSync}
                      onReplace={(s) => setPickerTarget({ type: 'replace', sessionId: s.id })}
                      onAddSession={(d) => setPickerTarget({ type: 'add', dayKey: d })}
                    />
                  ))}
                </div>
                <DragOverlay dropAnimation={null}>
                  <DragPreview session={activeDrag} />
                </DragOverlay>
              </DndContext>
            </>
          )}

          <p className='text-[10px] text-muted-foreground'>{t('periodization.week.timesheetHint')}</p>
        </DialogContent>
      </Dialog>

      <UserRunningExerciseEditorModal
        exerciseId={editUrexId}
        open={!!editUrexId}
        onOpenChange={(next) => {
          if (!next) setEditUrexId(null);
        }}
        onSaved={() => {
          load();
          reloadBlock();
        }}
      />

      <SessionPickerModal
        open={!!pickerTarget}
        onOpenChange={(next) => {
          if (!next) setPickerTarget(null);
        }}
        onSelect={handleSessionPicked}
        priority='stacked'
        titleKey={
          pickerTarget?.type === 'add'
            ? 'periodization.week.addSessionPickerTitle'
            : 'periodization.week.replaceSessionPickerTitle'
        }
        descriptionKey='training.sessionPicker.description'
      />
    </>
  );
}
